// English version of the Coptic (Egypt) cultural quiz — same ids/option order as miniGamesData.ts
import { QuizEn } from './types';

export const COPTE_EN: Record<string, QuizEn> = {
  // ── History ──
  'co-h-1': {
    q: "Which Egyptian queen, lover of Julius Caesar and then Mark Antony, is famous from Antiquity?",
    o: ["Cleopatra VII", "Nefertiti", "Hatshepsut", "Nefertari"],
    e: "Cleopatra VII Philopator (69–30 BC) was the last queen of the Ptolemaic dynasty of Egypt. She is famous for her relationships with Julius Caesar and Mark Antony.",
  },
  'co-h-2': {
    q: "Which female pharaoh ruled as a man, wearing a false beard and the title of pharaoh?",
    o: ["Hatshepsut", "Nefertiti", "Cleopatra", "Tiye"],
    e: "Hatshepsut (c. 1507–1458 BC) was one of the few women to take the full title of pharaoh. She is depicted with a false beard, a symbol of male kingship in Egypt.",
  },
  'co-h-3': {
    q: "Which pharaoh is considered the great temple builder, notably at Abu Simbel?",
    o: ["Ramesses II", "Tutankhamun", "Akhenaten", "Thutmose III"],
    e: "Ramesses II (c. 1303–1213 BC), nicknamed 'Ramesses the Great', reigned for about 67 years. He built the temples of Abu Simbel and signed one of the first peace treaties in history with the Hittites.",
  },
  'co-h-4': {
    q: "Which historical period corresponds to the reign of Akhenaten, who established a solar monotheism?",
    o: ["New Kingdom (c. 1350 BC)", "Old Kingdom", "Late Period", "Ptolemaic era"],
    e: "Akhenaten (c. 1353–1336 BC), presumed father of Tutankhamun, revolutionised Egyptian religion by establishing the exclusive worship of the Aten (sun disc), considered one of the first monotheisms.",
  },
  'co-h-5': {
    q: "In what year did Egypt gain independence from the United Kingdom?",
    o: ["1922", "1952", "1956", "1936"],
    e: "The Kingdom of Egypt was officially recognised as independent on 28 February 1922. However, a British military presence remained until the 1952 Revolution.",
  },
  'co-h-6': {
    q: "Who led the 1952 revolution that ended the monarchy in Egypt?",
    o: ["Gamal Abdel Nasser", "Anwar Sadat", "Mohamed Naguib", "Hosni Mubarak"],
    e: "The Free Officers movement, of which Nasser was the dominant figure, overthrew King Farouk in July 1952. Nasser later became president of the Arab Republic of Egypt.",
  },
  'co-h-7': {
    q: "Which 2011 event ended Hosni Mubarak's regime in Egypt?",
    o: ["The Arab Spring / Egyptian revolution", "A military coup", "A foreign invasion", "A voluntary resignation"],
    e: "The 2011 Egyptian revolution, part of the Arab Spring, saw millions of protesters fill Tahrir Square in Cairo. Hosni Mubarak resigned on 11 February 2011 after 30 years in power.",
  },
  'co-h-8': {
    q: "Which Macedonian conqueror founded Alexandria and ended the last Egyptian dynasty in 332 BC?",
    o: ["Alexander the Great", "Philip II of Macedon", "Ptolemy I", "Antigonus the One-Eyed"],
    e: "Alexander the Great (356–323 BC) conquered Egypt in 332 BC, ending the Persian period. He founded Alexandria in 331 BC. On his death, his general Ptolemy took control of Egypt and founded the Ptolemaic dynasty.",
  },
  'co-h-9': {
    q: "Which Arab caliph conquered Byzantine Egypt in 641 AD, transforming the country?",
    o: ["Umar ibn al-Khattab (through his general Amr ibn al-As)", "Abu Bakr", "Ali ibn Abi Talib", "Uthman ibn Affan"],
    e: "Caliph Umar ibn al-Khattab sent general Amr ibn al-As to conquer Byzantine Egypt in 639–641. The capture of Alexandria in 641 marks the start of Egypt's gradual Islamisation.",
  },
  'co-h-10': {
    q: "Which Kurdish-Egyptian sultan drove back the Crusaders and defeated the Mongols at the battle of Ain Jalut in 1260?",
    o: ["Baybars I (Mamluk sultan)", "Saladin (Salah ad-Din)", "Al-Mansur Qalawun", "Qutuz"],
    e: "Mamluk sultan Baybars I (reigned 1260–1277) consolidated the victory of Ain Jalut (1260), which halted the Mongol advance. He also drove the Crusaders out of many Levantine ports and strengthened Egypt's Mamluk empire.",
  },
  'co-h-11': {
    q: "Who built the famous Sphinx of Giza, according to the dominant archaeological theory?",
    o: ["Pharaoh Khafre (4th Dynasty)", "Pharaoh Khufu", "Pharaoh Ramesses II", "Pharaoh Thutmose IV"],
    e: "According to the dominant archaeological theory, the Great Sphinx of Giza was carved during the reign of Khafre (c. 2558–2532 BC), son of Khufu. The Sphinx's face is thought to represent Pharaoh Khafre.",
  },
  'co-h-12': {
    q: "Which Egyptian president nationalised the Suez Canal in 1956, triggering an international crisis?",
    o: ["Gamal Abdel Nasser", "Anwar Sadat", "Hosni Mubarak", "Mohamed Naguib"],
    e: "Gamal Abdel Nasser nationalised the Suez Canal on 26 July 1956, provoking the Suez Crisis. France, the United Kingdom and Israel attacked Egypt but had to withdraw under American and Soviet pressure.",
  },
  'co-h-13': {
    q: "Which Egyptian president signed the peace accords with Israel and was assassinated in 1981?",
    o: ["Anwar Sadat", "Gamal Abdel Nasser", "Hosni Mubarak", "Mohamed Naguib"],
    e: "Anwar Sadat signed the Egypt–Israel peace treaty in 1979, the first peace agreement between Israel and an Arab country. He received the Nobel Peace Prize in 1978 with Israeli Prime Minister Begin. He was assassinated by Islamists on 6 October 1981.",
  },
  'co-h-14': {
    q: "Which 1798 campaign by Napoleon Bonaparte brought European scholars to Egypt and contributed to deciphering hieroglyphs?",
    o: ["The Egyptian campaign (1798–1801)", "The Nile campaign", "The Syrian expedition", "The Ottoman campaign"],
    e: "Napoleon's Egyptian campaign (1798–1801) was a turning point: the 167 scholars of the Commission of Sciences and Arts documented ancient Egypt, leading to the publication of the 'Description de l'Égypte'. The Rosetta Stone, discovered in 1799, later made it possible to decipher hieroglyphs.",
  },

  // ── Politics ──
  'co-p-1': {
    q: "What is the capital of Egypt?",
    o: ["Cairo", "Alexandria", "Luxor", "Aswan"],
    e: "Cairo (Al-Qāhira) is the capital and largest city of Egypt and of Africa, with more than 20 million people in its metropolitan area.",
  },
  'co-p-2': {
    q: "What is Egypt's current political system?",
    o: ["Presidential republic", "Monarchy", "Theocracy", "Federation"],
    e: "Egypt is officially a presidential republic. Since 2014, Abdel Fattah el-Sisi has been its president, re-elected by large majorities in 2018 and 2024.",
  },
  'co-p-3': {
    q: "Which historic peace treaty did Egypt sign with Israel in 1979?",
    o: ["The Camp David Accords", "The Oslo Accords", "The Wadi Araba Treaty", "The Abraham Accords"],
    e: "The Camp David Accords (1978–1979), negotiated between Anwar Sadat and Menachem Begin with mediation by President Carter, led to the first peace treaty between Israel and an Arab country.",
  },
  'co-p-4': {
    q: "What is Egypt's official currency?",
    o: ["Egyptian pound", "Egyptian dollar", "Dinar", "Dirham"],
    e: "The Egyptian pound (EGP) is Egypt's official currency, divided into 100 piastres. It is issued by the Central Bank of Egypt.",
  },
  'co-p-5': {
    q: "Egypt is a member of which influential Arab organisation?",
    o: ["The Arab League", "GCC", "OPEC", "ASEAN"],
    e: "Egypt is one of the founding members of the Arab League, created in Cairo in 1945. The League's headquarters is in Cairo.",
  },
  'co-p-6': {
    q: "Which canal, opened in 1869, crosses Egypt and links the Mediterranean to the Red Sea?",
    o: ["The Suez Canal", "The Panama Canal", "The Nile Canal", "The Alexandria Canal"],
    e: "The Suez Canal, 193 km long, is one of the most important waterways in the world. Gamal Abdel Nasser nationalised it in 1956, triggering the Suez Crisis.",
  },
  'co-p-7': {
    q: "Which continent is Egypt part of?",
    o: ["Africa (and partly Asia via Sinai)", "Asia only", "Europe", "An independent Middle East"],
    e: "Egypt is mainly on the African continent, but the Sinai Peninsula is geographically in Asia. It is one of the few countries that spans two continents.",
  },
  'co-p-8': {
    q: "Which major infrastructure project did Egypt open in 2015, doubling the capacity of the Suez Canal?",
    o: ["New Suez Canal (expansion)", "New Capital Canal", "Nile–Red Sea Canal", "Alexandria–Aswan Canal"],
    e: "In August 2015, Egypt opened a major expansion of the Suez Canal including a new 35 km parallel section, allowing two-way traffic and reducing ships' waiting times.",
  },
  'co-p-9': {
    q: "Which Islamist organisation won Egypt's first free elections (2011–2012) before being overthrown?",
    o: ["The Muslim Brotherhood (Freedom and Justice)", "Al-Qaeda", "ISIS (Islamic State)", "Salafists of Al-Nour"],
    e: "The Freedom and Justice Party, the political arm of the Muslim Brotherhood, won the 2011–2012 parliamentary elections and Mohamed Morsi was elected president. He was overthrown by General el-Sisi on 3 July 2013.",
  },
  'co-p-10': {
    q: "Which water dispute pits Egypt against Ethiopia over the Grand Ethiopian Renaissance Dam?",
    o: ["Dispute over the Blue Nile waters", "The Nile War", "Red Sea oil conflict", "Sinai border war"],
    e: "The Grand Ethiopian Renaissance Dam (GERD), on the Blue Nile, is at the heart of a diplomatic and strategic dispute between Ethiopia, Egypt and Sudan. Egypt fears a reduced flow of the Nile, on which it depends for 90% of its fresh water.",
  },
  'co-p-11': {
    q: "What is Egypt's official religion and majority faith?",
    o: ["Sunni Islam", "Shia Islam", "Coptic Christianity", "Official secularism"],
    e: "Sunni Islam is Egypt's state religion and is practised by about 85–90% of the population. Coptic Christians make up about 10% of the population, the largest Christian community in the Middle East.",
  },
  'co-p-12': {
    q: "What is the name of the world's highest Sunni Islamic religious institution, based in Cairo?",
    o: ["Al-Azhar", "The Court of Mecca", "Dar al-Ifta", "The OIC"],
    e: "Al-Azhar University, founded in 970 under the Fatimids, is one of the oldest universities in the world and the leading authority of Sunni Islam. Its Grand Imam is a reference for hundreds of millions of Sunni Muslims.",
  },
  'co-p-13': {
    q: "What is the approximate population of Egypt, making it the most populous nation in the Arab world?",
    o: ["About 105 million (2023)", "About 50 million", "About 200 million", "About 30 million"],
    e: "With about 105 million inhabitants in 2023, Egypt is the most populous country in the Arab world and one of the most populous in Africa. More than 95% live in the Nile valley and delta.",
  },
  'co-p-14': {
    q: "In what year did Egypt join BRICS, broadening its international economic partnerships?",
    o: ["2024", "2022", "2020", "2019"],
    e: "Egypt officially joined BRICS (Brazil, Russia, India, China, South Africa) on 1 January 2024, along with Saudi Arabia, the UAE, Iran and Ethiopia, as part of the bloc's first enlargement.",
  },

  // ── Geography ──
  'co-g-1': {
    q: "Which river is essential to life and agriculture in Egypt?",
    o: ["The Nile", "The Euphrates", "The Jordan", "The Congo"],
    e: "The Nile is the longest river in the world (6,650 km). It crosses Egypt from south to north, forming a fertile valley set in the desert, the cradle of Egyptian civilisation.",
  },
  'co-g-2': {
    q: "Which great desert covers most of Egypt's territory?",
    o: ["The Sahara", "The Arabian Desert", "Sinai", "The Kalahari"],
    e: "About 90% of Egypt's territory is covered by the Sahara. The Nile valley and Delta are the country's main habitable and farming areas.",
  },
  'co-g-3': {
    q: "Which sea borders Egypt to the north?",
    o: ["The Mediterranean", "The Red Sea", "The Black Sea", "The Arabian Sea"],
    e: "Egypt is bordered to the north by the Mediterranean Sea, on which Alexandria, the country's second-largest city, stands.",
  },
  'co-g-4': {
    q: "Which sea borders Egypt to the east?",
    o: ["The Red Sea", "The Mediterranean", "The Caspian Sea", "The Indian Ocean"],
    e: "The Red Sea borders Egypt to the east, with the Sinai Peninsula at its northern end. Sharm el-Sheikh and Hurghada are well-known tourist destinations on this coast.",
  },
  'co-g-5': {
    q: "Where is the Nile Delta?",
    o: ["In the north of Egypt", "In the south", "In the centre", "In the east"],
    e: "The Nile Delta forms in northern Egypt, where the river branches out before flowing into the Mediterranean. It is the most fertile and densely populated area of the country.",
  },
  'co-g-6': {
    q: "Which artificial lake was created by building the Aswan Dam?",
    o: ["Lake Nasser", "Lake Nubia", "Lake Victory", "Lake Tana"],
    e: "Lake Nasser is a reservoir formed by the Aswan High Dam, opened in 1970. At 550 km long, it is one of the largest artificial lakes in the world.",
  },
  'co-g-7': {
    q: "Which Egyptian peninsula is geographically in Asia?",
    o: ["Sinai", "Libya", "Western Sahara", "Nubia"],
    e: "The Sinai Peninsula lies between the Gulf of Suez to the west and the Gulf of Aqaba to the east. Geographically part of Asia, it forms the land bridge between Africa and Asia.",
  },
  'co-g-8': {
    q: "Which large Egyptian oasis, reachable by road from Cairo, is famous for its hot springs and Greco-Roman ruins?",
    o: ["Siwa Oasis", "Dakhla Oasis", "Kharga Oasis", "Bahariya Oasis"],
    e: "Siwa Oasis, in the Western Desert near the Libyan border, is famous for its natural springs, the oracle of Amun (consulted by Alexander the Great in 331 BC) and its mud-brick architecture (kershef).",
  },
  'co-g-9': {
    q: "What is the area of Egypt?",
    o: ["1,002,000 km²", "500,000 km²", "2,000,000 km²", "300,000 km²"],
    e: "Egypt covers about 1,002,000 km², but less than 5% of the territory is inhabited and farmed (Nile valley and delta, oases). The rest is desert.",
  },
  'co-g-10': {
    q: "Which city is Egypt's main Red Sea port and a seaside tourist destination?",
    o: ["Hurghada", "Alexandria", "Port Said", "Suez"],
    e: "Hurghada (Al-Ghardaqa) is Egypt's main Red Sea resort, famous for its coral reefs, scuba diving and hotel complexes. It welcomes millions of European tourists every year.",
  },
  'co-g-11': {
    q: "Which arm of the Suez Canal forms the boundary between Africa and Asia?",
    o: ["The Suez Canal itself forms the boundary", "The Gulf of Suez", "The Gulf of Aqaba", "The Strait of Tiran"],
    e: "The Suez Canal, 193 km long, forms the geopolitical boundary between Africa (to the west) and Asia (to the east, the Sinai Peninsula). It is one of the few intercontinental boundaries in the world.",
  },
  'co-g-12': {
    q: "Which ancient capital of Egypt, now just a village, was nicknamed 'The White' (Memphis)?",
    o: ["Mit Rahina (ancient Memphis)", "El Minya", "Thebes", "Saqqara"],
    e: "Memphis (today Mit Rahina) was the first capital of unified Egypt, founded by Pharaoh Narmer around 3100 BC. It is now a UNESCO-listed archaeological site, with its statues of Ramesses II and its alabaster sphinx.",
  },
  'co-g-13': {
    q: "Which canal links the Nile to the Red Sea in the Suez region, allowing irrigation of Sinai?",
    o: ["The Ismailia Canal", "The Suez Canal", "The Tanis Canal", "The Toshka Canal"],
    e: "The Ismailia Canal is a freshwater canal linking the Nile to the Suez Canal and the city of Ismailia. It supplies fresh water to the canal zone, Sinai and the cities of Port Said and Suez.",
  },
  'co-g-14': {
    q: "Which huge Egyptian development project aims to irrigate new land in the desert?",
    o: ["Toshka Project (New Valley)", "Qattara Canal Project", "Saharan inland sea project", "Secondary Delta project"],
    e: "The Toshka Project (or New Valley), launched in the 1990s, aims to divert water from Lake Nasser towards the oases of the Western Desert to create a new farming area in the Toshka depression. Progress remains partial.",
  },
};
